import React from 'react';
import ModuleItem from 'src/components/LandingPage/Program/ModuleItem';
import Projects from 'src/components/LandingPage/Program/Projects';
import ButtonScrollToForm from 'src/components/LandingPage/ButtonScrollToForm';
import ProgramData from 'src/data/Program';
import colors from 'src/style/colors';
import { mediaQueries } from 'src/style/breakpoints';
import Comet from 'src/components/Illustrations/Comet.svg';

const TAG_COLORS = {
  yellow: '#F5DD27',
  white: '#FFFFFF',
  green: '#4DB33D',
  red: '#F28362',
  blueDark: '#264DE4',
  blue: '#7BA4DB',
  blueLight: '#61DAFB',
  orange: '#FBBF23',
  grey: '#AAAAAA',
};

const programCards = [
  {
    number: '01',
    duration: '2 SEMAINES',
    title: 'Les bases du JavaScript',
    description: 'Commencez à coder vos premières lignes d\u2019algo en JavaScript\u00A0!',
    tags: [
      { text: 'JavaScript', color: TAG_COLORS.yellow },
      { text: 'Node.js', color: TAG_COLORS.white },
      { text: 'Terminal', color: TAG_COLORS.white },
    ],
  },
  {
    number: '02',
    duration: '1 SEMAINE',
    title: 'Serveur / API / Bases de données',
    description: 'Vous serez en mesure de comprendre le fonctionnement d\u2019un serveur.',
    tags: [
      { text: 'Express', color: TAG_COLORS.white },
      { text: 'MongoDB', color: TAG_COLORS.green },
      { text: 'GitHub', color: TAG_COLORS.white },
    ],
  },
  {
    number: '03',
    duration: '1 SEMAINE',
    title: 'Intégration HTML5 / CSS3',
    description: 'Vous apprendrez les meilleures pratiques de mise en page et d\u2019optimisation Web.',
    tags: [
      { text: 'HTML', color: TAG_COLORS.red },
      { text: 'CSS', color: TAG_COLORS.blueDark },
      { text: 'JavaScript', color: TAG_COLORS.yellow },
    ],
  },
  {
    number: '04',
    duration: '3 SEMAINES',
    title: 'Interfaces Web dynamiques',
    description:
      'Avec React et son système de composants, vous pourrez créer rapidement des interfaces ultra-puissantes.',
    tags: [
      { text: 'React', color: TAG_COLORS.blueLight },
      { text: 'ViteJS', color: TAG_COLORS.blueLight },
      { text: 'Cloudinary', color: TAG_COLORS.blue },
    ],
  },
  {
    number: '05',
    duration: '1 SEMAINE',
    durationSuffix: '✦ IA',
    durationSuffixColor: TAG_COLORS.orange,
    title: 'Coder avec l\u2019intelligence artificielle',
    description:
      'Découvrez comment utiliser l\u2019IA pour coder plus vite, mieux, et avec plus d\u2019impact.',
    tags: [
      { text: 'Cursor', color: TAG_COLORS.orange },
      { text: 'ChatGPT', color: TAG_COLORS.orange },
      { text: 'Claude', color: TAG_COLORS.orange },
      { text: 'Mistral AI', color: TAG_COLORS.white },
    ],
  },
  {
    number: '06',
    duration: '2 SEMAINES',
    durationSuffix: '✦ IA',
    durationSuffixColor: TAG_COLORS.orange,
    title: 'Création d\u2019un projet intégrant l\u2019IA',
    description:
      'Concevez un projet personnel ou en équipe en intégrant des outils d\u2019intelligence artificielle.',
    tags: [
      { text: 'React', color: TAG_COLORS.blueLight },
      { text: 'Cursor', color: TAG_COLORS.orange },
      { text: 'ChatGPT', color: TAG_COLORS.orange },
    ],
  },
  {
    number: '07',
    duration: 'EN E-LEARNING',
    title: 'Applications Mobile iOS et Android',
    description:
      'React Native vous permettra de réaliser des applications Mobile \u00AB\u00A0cross-platform\u00A0\u00BB, pour iOS et Android.',
    tags: [
      { text: 'React Native', color: TAG_COLORS.white },
      { text: 'Expo', color: TAG_COLORS.blueLight },
      { text: 'Cloudinary', color: TAG_COLORS.blue },
    ],
  },
  {
    number: '→',
    duration: 'ET APRÈS ?',
    title: 'Poursuite en stage ou en alternance',
    description:
      'À l\u2019issue du bootcamp, certains de nos élèves poursuivent avec un stage ou rejoignent notre formation en alternance de 18 mois pour aller encore plus loin.',
    tags: [
      { text: 'Alternance 18 mois', color: TAG_COLORS.orange },
      { text: 'Stage', color: TAG_COLORS.grey },
    ],
  },
];

const ProgramCard = ({ card }) => (
  <div
    css={{
      backgroundColor: 'rgba(138, 125, 222, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 16,
      padding: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      aspectRatio: '1 / 1',
      overflow: 'hidden',
      [mediaQueries.phoneOnly]: {
        aspectRatio: 'auto',
      },
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(138, 125, 222, 0.25)',
        transform: 'translateY(-4px)',
      },
    }}
  >
    {/* Number */}
    <span
      css={{
        fontSize: '2rem',
        fontWeight: 800,
        color: 'rgba(255, 255, 255, 0.25)',
        lineHeight: 1,
      }}
    >
      {card.number}
    </span>

    {/* Duration */}
    <span
      css={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: 'rgba(255, 255, 255, 0.7)',
      }}
    >
      {card.duration}
      {card.durationSuffix && (
        <>
          {' · '}
          <span css={{ color: card.durationSuffixColor }}>{card.durationSuffix}</span>
        </>
      )}
    </span>

    {/* Title */}
    <h3
      css={{
        fontSize: '1.1rem',
        fontWeight: 700,
        color: colors.white,
        margin: 0,
        lineHeight: 1.3,
      }}
    >
      {card.title}
    </h3>

    {/* Description */}
    <p
      css={{
        fontSize: 13,
        lineHeight: 1.6,
        color: 'rgba(255, 255, 255, 0.7)',
        margin: 0,
      }}
    >
      {card.description}
    </p>

    {/* Tags */}
    <div css={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
      {card.tags.map((tag, i) => (
        <span
          key={i}
          css={{
            fontSize: 13,
            fontWeight: 600,
            color: tag.color,
            backgroundColor: `${tag.color}18`,
            border: `1px solid ${tag.color}40`,
            padding: '4px 12px',
            borderRadius: 50,
          }}
        >
          {tag.text}
        </span>
      ))}
    </div>
  </div>
);

const ProgramSectionRedesign = ({ fromPage, durationLabel, subtitle }) => {
  const isFullTime = fromPage === 'rncp6-fullTime';

  // Legacy: filter from ProgramData for non-fulltime pages
  const programFiltered = !isFullTime
    ? ProgramData.filter((item) => item.courses.indexOf(fromPage + '-landing') !== -1)
    : [];

  if (!isFullTime) {
    return (
      <section
        css={{
          padding: '5rem 2rem',
          backgroundColor: colors.white,
          position: 'relative',
          [mediaQueries.phoneOnly]: {
            padding: '3rem 1rem',
          },
        }}
      >
        <div css={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2
            css={{
              textAlign: 'center',
              fontSize: '2.5rem',
              marginBottom: '3rem',
              color: colors.purpleDark,
              fontWeight: 700,
              [mediaQueries.phoneOnly]: {
                fontSize: '1.75rem',
                marginBottom: '2rem',
              },
            }}
          >
            Ce que vous maîtriserez après{' '}
            <span css={{ color: colors.purple }}>{durationLabel}</span> de formation
          </h2>
          {subtitle && (
            <p
              css={{
                textAlign: 'center',
                fontSize: '1.25rem',
                color: colors.darkGrey,
                marginTop: '-2rem',
                marginBottom: '3rem',
                [mediaQueries.phoneOnly]: {
                  fontSize: '1.1rem',
                  marginTop: '-1.25rem',
                  marginBottom: '2rem',
                },
              }}
            >
              {subtitle}
            </p>
          )}
          <div
            css={{
              display: 'flex',
              width: '100%',
              gap: 30,
              flexWrap: 'wrap',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            {programFiltered.map((e) => (
              <ModuleItem key={e.title} item={e} fromPage={fromPage} />
            ))}
          </div>
          <div
            css={{
              marginTop: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              [mediaQueries.desktopUp]: {
                position: 'absolute',
                right: 90,
                top: 80,
              },
            }}
          >
            <img
              src={Comet}
              alt="illustration comète"
              css={{
                width: '100%',
                maxWidth: '50%',
                [mediaQueries.tabletLandscapeUp]: { maxWidth: 400 },
              }}
            />
          </div>
          <Projects />
          <ButtonScrollToForm text={`Je postule\u00A0!`} />
        </div>
      </section>
    );
  }

  return (
    <section
      css={{
        padding: '5rem 2rem',
        backgroundColor: colors.purple,
        position: 'relative',
        overflow: 'hidden',
        [mediaQueries.phoneOnly]: {
          padding: '3rem 1rem',
        },
      }}
    >
      {/* Background glow */}
      <div
        css={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          height: 800,
          background: 'radial-gradient(circle, rgba(138, 125, 222, 0.4) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <div
        css={{
          maxWidth: 1300,
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Section Title */}
        <h2
          css={{
            textAlign: 'center',
            fontSize: '2.5rem',
            marginBottom: '1rem',
            color: colors.white,
            fontWeight: 700,
            [mediaQueries.phoneOnly]: {
              fontSize: '1.75rem',
            },
          }}
        >
          Ce que vous maîtriserez après{' '}
          <span css={{ color: colors.yellow }}>{durationLabel}</span>
        </h2>

        {subtitle && (
          <p
            css={{
              textAlign: 'center',
              fontSize: '1.15rem',
              color: 'rgba(255, 255, 255, 0.6)',
              marginBottom: '3rem',
              [mediaQueries.phoneOnly]: {
                fontSize: '1rem',
                marginBottom: '2rem',
              },
            }}
          >
            {subtitle}
          </p>
        )}

        {/* Cards Grid */}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 280px)',
            justifyContent: 'center',
            gap: '1.25rem',
            '@media only screen and (max-width: 1199px)': {
              gridTemplateColumns: 'repeat(2, 240px)',
            },
            [mediaQueries.phoneOnly]: {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          {programCards.map((card, index) => (
            <ProgramCard key={index} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgramSectionRedesign;
